import { Crown, BadgeCheck, Star, Clock, CheckCheck, XCircle, Heart, Award, ShieldCheck, Zap, FileText } from "lucide-react";

/* Badge palette — Charte Helencia 2026
   Premium = caramel doré
   Vérifié = beige foncé
   En attente = nude clair
   Accepté = vert doux
   Refusé = rouge discret
*/

export function PremiumBadge({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-[#FAF3EA] to-[#F3DDBD] text-[#8B5A2B] border border-[#E6C29A] ${className}`}
      data-testid="premium-badge"
    >
      <Crown className="w-3 h-3" />
      Premium
    </span>
  );
}

export function VerifiedBadge({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F0E0C7] text-[#6E4B2A] border border-[#D4A574] ${className}`}
      data-testid="verified-badge"
    >
      <BadgeCheck className="w-3 h-3" />
      Vérifié
    </span>
  );
}

export function LevelBadge({ level, className = "" }) {
  const colors = {
    "Débutant":        "bg-[#FAF3EA] text-[#8C7D6B] border-[#EADDC9]",
    "Talent actif":    "bg-[#F0E0C7] text-[#6E4B2A] border-[#E6C29A]",
    "Talent confirmé": "bg-[#E6C29A]/40 text-[#6E4B2A] border-[#D4A574]",
    "Premium":         "bg-gradient-to-r from-[#FAF3EA] to-[#F3DDBD] text-[#8B5A2B] border-[#E6C29A]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors[level] || colors["Débutant"]} ${className}`}
      data-testid="level-badge"
    >
      <Star className="w-3 h-3" />
      {level}
    </span>
  );
}

export function PendingBadge({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#FAF3EA] text-[#A26F3E] border border-[#EADDC9] ${className}`}
      data-testid="pending-badge"
    >
      <Clock className="w-3 h-3" />
      En attente
    </span>
  );
}

export function AcceptedBadge({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E7F0E3] text-[#3F6A39] border border-[#BFD4B7] ${className}`}
      data-testid="accepted-badge"
    >
      <CheckCheck className="w-3 h-3" />
      Accepté
    </span>
  );
}

export function RatingPill({ rating = 0, count = 0, className = "" }) {
  if (!rating || count === 0) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#1A1A1A] text-[#D4A574] ${className}`}
      data-testid="rating-pill"
      title={`${count} avis`}
    >
      <Star className="w-3 h-3 fill-[#D4A574] text-[#D4A574]" />
      {rating.toFixed(1)}
      <span className="text-white/40 font-normal">·{count}</span>
    </span>
  );
}

const RELIABILITY_ICON = {
  Clock: Clock,
  Heart: Heart,
  Award: Award,
  ShieldCheck: ShieldCheck,
  Zap: Zap,
  FileText: FileText,
};

export function ReliabilityBadge({ badge, className = "" }) {
  const Icon = RELIABILITY_ICON[badge?.icon] || Award;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#FAF3EA] text-[#6E4B2A] border border-[#E6C29A] ${className}`}
      data-testid={`reliability-badge-${badge?.id}`}
    >
      <Icon className="w-3 h-3" />
      {badge?.label}
    </span>
  );
}

export function ReliabilityRow({ badges = [], rating = 0, count = 0, max = 3, className = "" }) {
  if (!badges?.length && !count) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="reliability-row">
      <RatingPill rating={rating} count={count} />
      {badges.slice(0, max).map((b) => (
        <ReliabilityBadge key={b.id} badge={b} />
      ))}
    </div>
  );
}

export function RefusedBadge({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F6E6E3] text-[#A3483F] border border-[#E6C0BA] ${className}`}
      data-testid="refused-badge"
    >
      <XCircle className="w-3 h-3" />
      Refusé
    </span>
  );
}
